import type { Query, QueryKey } from '@tanstack/react-query';
import type { WhatsAppMessageContext } from 'src/modules/shared/messaging/types/whatsapp-message-context.type';
import {
  getTimelineSearchParams,
  valueMatches,
} from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import { cancelQueryReads } from 'src/modules/shared/utils/api/cancel-query-reads.util';

/**
 * Identifies timeline reads whose explicit CRM scope contains this submission.
 * Search text and other predicates do not prove insertion eligibility, but a
 * matching identity still needs stale-response protection during the send.
 * @param key - Cached timeline search key.
 * @param context - Submission identities established by the consuming feature.
 * @returns Whether this mutation supersedes the scoped read.
 */
export const isMessageMutationTimeline = (
  key: QueryKey,
  context: WhatsAppMessageContext
): boolean => {
  if (key[0] !== 'crm/timeline-items/search') return false;
  const params = getTimelineSearchParams(key);
  const identity = context.params.optimisticData;
  if (!params || !identity?.funnelConversationId) return false;
  const ids: Record<string, string | undefined> = {
    funnelConversationId: identity.funnelConversationId.toString(),
    contactId: identity.contactId?.toString(),
    funnelId: identity.funnelId?.toString(),
  };
  const filters = params.filters ?? [];
  if (filters.some((filter) => filter.or)) return false;
  const scopes = filters.filter(
    (filter) => filter.field && filter.field in ids
  );
  return (
    scopes.length > 0 &&
    scopes.every(
      (filter) =>
        (!filter.operator || ['eq', 'in'].includes(filter.operator)) &&
        valueMatches(ids[filter.field ?? ''], filter.value)
    )
  );
};

/**
 * Cancels only this submission's affected CRM reads, preserving unrelated loaders.
 * @param context - Captured submission and account-owned query cache.
 * @returns Interrupted empty reads that need restarting when the send settles.
 */
export const cancelMessageMutationReads = (
  context: WhatsAppMessageContext
): Query[] => {
  const filters = {
    predicate: (query: Query) =>
      isMessageMutationTimeline(query.queryKey, context),
  };
  const initialReads = context.queryClient
    .getQueryCache()
    .findAll(filters)
    .filter(
      (query) =>
        query.state.data === undefined && query.state.fetchStatus !== 'idle'
    );
  cancelQueryReads(context.queryClient, filters);
  return initialReads;
};
